import {
  DIAS_DE_LA_SEMANA,
  type Frecuencia,
  hoyLocal,
  NOMBRE_TIPO_DE_FRECUENCIA,
  TIPOS_DE_FRECUENCIA,
  type TipoDeFrecuencia,
} from "@gestiondoc/shared";

/**
 * "Cada cuánto": el selector de la frecuencia de una diligencia (2026-10-20). Un desplegable con
 * el tipo y, según el tipo, el día de la semana, el día del mes o cada cuántos días y desde
 * cuándo. Lo usan Usuarios (ficha de la persona), la página de hojas del formato ("Lo llenan
 * todos los días") y "Cargar formato existente".
 */

const CLASE =
  "min-h-[36px] rounded-md border border-borde bg-white px-2 text-[12.5px] outline-none focus:border-marino disabled:opacity-60";

/** La frecuencia con que arranca cada tipo al elegirlo en el desplegable. */
function porDefecto(tipo: TipoDeFrecuencia, anterior: Frecuencia): Frecuencia {
  switch (tipo) {
    case "diaria":
      return { tipo };
    case "semanal":
      return { tipo, diaSemana: anterior.tipo === "semanal" ? anterior.diaSemana : 1 };
    case "mensual":
      return { tipo, diaMes: anterior.tipo === "mensual" ? anterior.diaMes : 1 };
    case "cada_n_dias":
      return anterior.tipo === "cada_n_dias"
        ? anterior
        : { tipo, cadaDias: 2, desde: hoyLocal().toISOString().slice(0, 10) };
  }
}

export function SelectorFrecuencia({
  valor,
  onChange,
  disabled = false,
  etiqueta = "Cada cuánto",
}: {
  valor: Frecuencia;
  onChange: (f: Frecuencia) => void;
  disabled?: boolean;
  /** El `aria-label` del desplegable del tipo. */
  etiqueta?: string;
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <select
        value={valor.tipo}
        aria-label={etiqueta}
        disabled={disabled}
        onChange={(e) => onChange(porDefecto(e.target.value as TipoDeFrecuencia, valor))}
        className={CLASE}
      >
        {TIPOS_DE_FRECUENCIA.map((t) => (
          <option key={t} value={t}>
            {NOMBRE_TIPO_DE_FRECUENCIA[t]}
          </option>
        ))}
      </select>
      {valor.tipo === "semanal" && (
        <select
          value={valor.diaSemana}
          aria-label="Día de la semana"
          disabled={disabled}
          onChange={(e) => onChange({ tipo: "semanal", diaSemana: Number(e.target.value) })}
          className={CLASE}
        >
          {DIAS_DE_LA_SEMANA.map((d, i) => (
            <option key={d} value={i + 1}>
              los {d.endsWith("o") ? `${d}s` : d}
            </option>
          ))}
        </select>
      )}
      {valor.tipo === "mensual" && (
        <select
          value={valor.diaMes}
          aria-label="Día del mes"
          disabled={disabled}
          onChange={(e) => onChange({ tipo: "mensual", diaMes: Number(e.target.value) })}
          className={CLASE}
        >
          {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
            <option key={d} value={d}>
              {d === 31 ? "el último día" : `el día ${d}`}
            </option>
          ))}
        </select>
      )}
      {valor.tipo === "cada_n_dias" && (
        <>
          <span className="text-[12.5px] text-texto-2">cada</span>
          <input
            type="number"
            min={2}
            max={366}
            value={valor.cadaDias}
            aria-label="Cada cuántos días"
            disabled={disabled}
            onChange={(e) =>
              onChange({ ...valor, cadaDias: Math.max(2, Number(e.target.value) || 2) })
            }
            className={`${CLASE} w-[64px]`}
          />
          <span className="text-[12.5px] text-texto-2">días desde el</span>
          <input
            type="date"
            value={valor.desde}
            aria-label="Desde qué día"
            disabled={disabled}
            onChange={(e) => onChange({ ...valor, desde: e.target.value })}
            className={CLASE}
          />
        </>
      )}
    </span>
  );
}
